import { MatchPlay_GameManager } from "./MatchPlay_GameManager";
import { Base_GameManager } from "./Base_GameManager";
export class StrokePlay_GameManager extends Base_GameManager {
    constructor(rc, gc) {
        super(rc, gc);
        // ---------- Stored (IStored_GameManagerData) ----------
        /** Optional extra identifier; not part of IStored_GameManagerData but useful. */
        this.id = "StrokePlay";
        /** The players involved in this game (for Stroke Play: usually a single ID). */
        this.players = [];
        /** Friendly name, e.g. "Stroke Play". */
        this.name = "Stroke Play";
        /** Par for each hole (copied from IRoundContext). */
        this.pars = [];
        /** Handicap ranking per hole (copied from IRoundContext). */
        this.handicaps = [];
        this.rc = rc;
        this.gc = gc;
        this.players = gc.players.map((p) => rc.roster[p.rosterIndex].id);
        // Copy course metadata into this manager
        this.pars = rc.pars?.slice() ?? [];
        this.handicaps = rc.handicaps?.slice() ?? [];
    }
    // ---------------------------------------------------------------------------
    // Static: create all stroke-play managers for this round
    // ---------------------------------------------------------------------------
    /**
     * Create one StrokePlay_GameManager per roster entry.
     *
     * Each manager tracks a single player's stroke-play performance:
     *   - gc.players = [IPlayer] for that roster entry
     *   - header     = that player's ID (e.g., "J")
     */
    static CreateGameManagers(rc, scorecardData) {
        const managers = [];
        const roster = rc.roster ?? [];
        const holes = rc.holesInRound ?? 18;
        for (let rosterIndex = 0; rosterIndex < roster.length; rosterIndex++) {
            const entry = roster[rosterIndex];
            // Handicap strokes for this player
            const strokesReceived = entry.handicap ?? 0;
            // Reuse the MatchPlay allocation helper so we have a single source of truth
            const strokesPerHole = MatchPlay_GameManager.allocateStrokesPerHole(strokesReceived, holes, rc.handicaps ?? []);
            const player = {
                id: entry.id, // memberId
                nickName: entry.nickName,
                displayCode: entry.displayCode, // J
                rosterIndex,
                strokesReceived,
                strokesPerHole,
                cumulativeGrossScore: new Array(holes).fill(0),
                cumulativeNetScore: new Array(holes).fill(0),
            };
            const gc = {
                players: [player],
                header: entry.displayCode, // display J, not memberId
                scoreDisplaysByHole: new Array(holes).fill(null),
                skinsCarriedForward: 0,
            };
            managers.push(new StrokePlay_GameManager(rc, gc));
        }
        return managers;
    }
    getScore(displayType, holeIndex, playerScores, currentPlayerId) {
        void currentPlayerId;
        if (holeIndex === undefined) {
            return displayType === "scorecard-game-header" ? this.gc.header : "";
        }
        const strokeScore = this.getStrokeScore(playerScores, holeIndex);
        const formattedCellScore = strokeScore
            ? this.formatForScorecardCell(strokeScore)
            : "";
        switch (displayType) {
            case "scorecard-game-cell":
                return [formattedCellScore];
            case "scorecard-game-header":
                return this.gc.header;
            case "index-page":
                return strokeScore
                    ? this.formatStrokeScoreForPlayer(strokeScore, displayType)
                    : "";
            default:
                return "";
        }
    }
    /**
     * Builds the stroke-play score state through holeIndex.
     * Formatting is intentionally left to getScore's formatter calls.
     */
    getStrokeScore(playerScores, holeIndex) {
        if (!playerScores)
            return undefined;
        const playerId = this.gc.players[0]?.id;
        if (!playerId)
            return undefined;
        const playerScore = playerScores.find((p) => p.playerId === playerId);
        if (!playerScore)
            return undefined;
        const currentHoleGross = playerScore.gross[holeIndex];
        // Like MatchPlay cells: this score is blank unless this hole is scored.
        if (!currentHoleGross)
            return undefined;
        let gross = 0;
        let strokesGiven = 0;
        let parTotal = 0;
        const strokesPerHole = this.gc.players[0]?.strokesPerHole ?? [];
        for (let i = 0; i <= holeIndex; i++) {
            const holeGross = playerScore.gross[i];
            // Only count holes that have a gross score entered.
            if (!holeGross)
                continue;
            gross += holeGross;
            strokesGiven += strokesPerHole[i] ?? 0;
            parTotal += this.pars[i] ?? 0;
        }
        const net = gross - strokesGiven;
        const vsPar = net - parTotal;
        return this.buildStrokeScore(playerId, gross, net, vsPar);
    }
    buildStrokeScore(playerId, gross, net, vsPar) {
        return {
            playerId,
            gross,
            net,
            vsPar,
        };
    }
    formatForScorecardCell(strokeScore) {
        return `${strokeScore.gross} (${strokeScore.net})`;
    }
    formatStrokeScoreForPlayer(strokeScore, displayType) {
        const displayCode = this.gc.players.find(p => p.id === strokeScore.playerId)?.displayCode
            ?? strokeScore.playerId;
        const vsPar = strokeScore.vsPar === 0
            ? "-0"
            : strokeScore.vsPar > 0
                ? `+${strokeScore.vsPar}`
                : `${strokeScore.vsPar}`;
        return `${displayCode}${strokeScore.gross}(${strokeScore.net})${vsPar}`;
    }
    getStrokesOnHole(holeIndex) {
        // gc and players come from Base_GameManager / constructor
        const gc = this.gc;
        let maxStrokes = 0;
        for (const p of gc.players) {
            const strokes = p.strokesPerHole[holeIndex] ?? 0;
            if (strokes > maxStrokes)
                maxStrokes = strokes;
        }
        return maxStrokes;
    }
    /** Returns this manager's participant IDs (read-only view). */
    getParticipants() {
        return this.players;
    }
    /** True if this manager involves the given player ID. */
    includesPlayer(id) {
        return this.players.includes(id);
    }
    /**
     * Returns one consolidated Stroke Play score line for all StrokePlay managers.
     *
     * This is the game-type aggregation layer:
     * - each manager instance still owns its own getScore(...) result
     * - this static method sorts the player scores by score versus par
     * - this static method joins those player scores into one "SP:" line
     */
    static getAllScoresForGameType(displayScoreType, managers, holeIndex, playerScores, currentPlayerId) {
        void currentPlayerId;
        const strokePlayManagers = managers.filter((gm) => gm instanceof StrokePlay_GameManager);
        const line = strokePlayManagers
            .map((gm) => {
            const strokeScore = gm.getStrokeScore(playerScores, holeIndex);
            const text = gm.getScore(displayScoreType, holeIndex, playerScores);
            if (!strokeScore || typeof text !== "string" || text === "") {
                return null;
            }
            return {
                sortValue: strokeScore.vsPar,
                text,
            };
        })
            .filter((r) => r !== null)
            .sort((a, b) => a.sortValue - b.sortValue)
            .map((r) => r.text)
            .join("  ");
        return line ? `SP: ${line}` : "";
    }
    getGameSummary_v2(holeIndex, playerScores) {
        if (!playerScores) {
            return undefined;
        }
        const player = this.gc.players[0];
        if (!player) {
            return undefined;
        }
        const playerScore = playerScores.find(score => score.playerId === player.id);
        if (!playerScore) {
            return undefined;
        }
        const currentHoleGross = playerScore.gross[holeIndex];
        if (!currentHoleGross) {
            return undefined;
        }
        let grossTotal = 0;
        let strokesReceived = 0;
        let parTotal = 0;
        for (let i = 0; i <= holeIndex; i++) {
            const holeGross = playerScore.gross[i];
            if (!holeGross) {
                continue;
            }
            grossTotal += holeGross;
            strokesReceived += player.strokesPerHole[i] ?? 0;
            parTotal += this.pars[i] ?? 0;
        }
        const netTotal = grossTotal - strokesReceived;
        const grossToPar = grossTotal - parTotal;
        const netToPar = netTotal - parTotal;
        const holePar = this.pars[holeIndex] ?? 0;
        return {
            playerName: player.nickName,
            holeScore: currentHoleGross,
            holeResult: this.getHoleResult_v2(currentHoleGross, holePar),
            grossTotal,
            netTotal,
            grossToPar,
            netToPar,
        };
    }
    getHoleResult_v2(gross, par) {
        const difference = gross - par;
        if (difference <= -2) {
            return "eagle";
        }
        if (difference === -1) {
            return "birdie";
        }
        if (difference === 0) {
            return "par";
        }
        if (difference === 1) {
            return "bogey";
        }
        if (difference === 2) {
            return "double-bogey";
        }
        return "other";
    }
    static getGameSummaries(managers, holeIndex, playerScores) {
        const strokePlayManagers = managers.filter((gm) => gm instanceof StrokePlay_GameManager);
        const players = strokePlayManagers
            .map(gm => gm.getGameSummary_v2(holeIndex, playerScores))
            .filter((summary) => summary !== undefined)
            .sort((a, b) => a.netToPar - b.netToPar);
        const summary = {
            holeNumber: holeIndex + 1,
            players,
        };
        // console.log(
        //   "StrokePlay_GameManager.getGameSummaries",
        //   summary
        // );
        return summary;
    }
    static getAvGameSummary(managers, holeIndex, playerScores) {
        return {
            strokePlay: {
                manifest: {},
                data: this.getGameSummaries(managers, holeIndex, playerScores),
            },
        };
    }
}
//# sourceMappingURL=StrokePlay_GameManager.js.map